import { z } from 'zod';
import { apiEnvelope } from '../../envelope';

export const adaptivePhqGadV2AssessmentDefinitionId =
  '54800000-0000-4000-8000-000000000001' as const;

export const phq4QuestionIds = ['phq2_q1', 'phq2_q2', 'gad2_q1', 'gad2_q2'] as const;

export const phq9ExtensionQuestionIds = [
  'phq9_q3',
  'phq9_q4',
  'phq9_q5',
  'phq9_q6',
  'phq9_q7',
  'phq9_q8',
  'phq9_q9',
] as const;

export const gad7ExtensionQuestionIds = [
  'gad7_q3',
  'gad7_q4',
  'gad7_q5',
  'gad7_q6',
  'gad7_q7',
] as const;

export const sessionSuicideScreenQuestionId = 'session_suicide_screen' as const;

export const assessmentQuestionIds = [
  ...phq4QuestionIds,
  ...phq9ExtensionQuestionIds,
  ...gad7ExtensionQuestionIds,
  sessionSuicideScreenQuestionId,
] as const;

export const assessmentQuestionIdSchema = z.enum(assessmentQuestionIds);
export type AssessmentQuestionId = z.infer<typeof assessmentQuestionIdSchema>;

export const assessmentAnswerValueSchema = z.number().int().min(0).max(3);
export type AssessmentAnswerValue = z.infer<typeof assessmentAnswerValueSchema>;

// A partial map from question id to persisted answer value. Keys are trusted
// server-produced question ids, so a plain string-keyed record (rather than the
// enum-keyed record, which would demand every question id be present) captures
// the partial nature of an in-progress assessment.
export const assessmentAnswerMapSchema = z.record(z.string(), assessmentAnswerValueSchema);
export type AssessmentAnswerMap = z.infer<typeof assessmentAnswerMapSchema>;

export const assessmentAnswerSchema = z
  .object({
    value: assessmentAnswerValueSchema,
  })
  .strict();
export type AssessmentAnswer = z.infer<typeof assessmentAnswerSchema>;

export const assessmentExtensionSchema = z.enum(['phq9', 'gad7']);
export type AssessmentExtension = z.infer<typeof assessmentExtensionSchema>;

export const assessmentResultKeys = ['no_mild', 'moderate', 'severe', 'suicide_risk'] as const;
export const assessmentResultKeySchema = z.enum(assessmentResultKeys);
export type AssessmentResultKey = z.infer<typeof assessmentResultKeySchema>;

export const assessmentSessionStatuses = ['active', 'completed'] as const;
export const assessmentSessionStatusSchema = z.enum(assessmentSessionStatuses);
export type AssessmentSessionStatus = z.infer<typeof assessmentSessionStatusSchema>;

export const assessmentFlowVersions = ['v2'] as const;
export const assessmentFlowVersionSchema = z.enum(assessmentFlowVersions);
export type AssessmentFlowVersion = z.infer<typeof assessmentFlowVersionSchema>;

export const assessmentScoreSummarySchema = z
  .object({
    phq2_subscore: z.number().int().min(0).max(6).nullable(),
    gad2_subscore: z.number().int().min(0).max(6).nullable(),
    phq9_total: z.number().int().min(0).max(27).nullable(),
    gad7_total: z.number().int().min(0).max(21).nullable(),
  })
  .strict();
export type AssessmentScoreSummary = z.infer<typeof assessmentScoreSummarySchema>;

export const assessmentSessionSchema = z
  .object({
    session_id: z.string().uuid(),
    status: assessmentSessionStatusSchema,
    flow_version: assessmentFlowVersionSchema,
    started_at: z.string().datetime(),
    completed_at: z.string().datetime().nullable(),
    extensions: z.array(assessmentExtensionSchema),
    result_key: assessmentResultKeySchema.nullable(),
    scores: assessmentScoreSummarySchema,
  })
  .strict();
export type AssessmentSession = z.infer<typeof assessmentSessionSchema>;

export const startAssessmentRequestSchema = z.object({}).strict();
export type StartAssessmentRequest = z.infer<typeof startAssessmentRequestSchema>;

export const startAssessmentDataSchema = z
  .object({
    session: assessmentSessionSchema,
    resumed: z.boolean(),
    next_step_key: z.string().min(1),
    answers: assessmentAnswerMapSchema,
  })
  .strict();
export type StartAssessmentData = z.infer<typeof startAssessmentDataSchema>;
export const startAssessmentResponseSchema = apiEnvelope(startAssessmentDataSchema);

export const activeAssessmentDataSchema = z
  .object({
    session: assessmentSessionSchema.nullable(),
    next_step_key: z.string().min(1).nullable(),
    answers: assessmentAnswerMapSchema,
  })
  .strict();
export type ActiveAssessmentData = z.infer<typeof activeAssessmentDataSchema>;
export const activeAssessmentResponseSchema = apiEnvelope(activeAssessmentDataSchema);

export const saveAssessmentAnswerRequestSchema = z
  .object({
    session_id: z.string().uuid(),
    question_id: assessmentQuestionIdSchema,
    answer: assessmentAnswerSchema,
  })
  .strict();
export type SaveAssessmentAnswerRequest = z.infer<typeof saveAssessmentAnswerRequestSchema>;

export const saveAssessmentAnswerDataSchema = z
  .object({
    session: assessmentSessionSchema,
    question_id: assessmentQuestionIdSchema,
    saved_at: z.string().datetime(),
  })
  .strict();
export type SaveAssessmentAnswerData = z.infer<typeof saveAssessmentAnswerDataSchema>;
export const saveAssessmentAnswerResponseSchema = apiEnvelope(saveAssessmentAnswerDataSchema);

export const evaluateAssessmentDataSchema = z
  .object({
    session: assessmentSessionSchema,
    phq2_subscore: z.number().int().min(0).max(6),
    gad2_subscore: z.number().int().min(0).max(6),
    extensions: z.array(assessmentExtensionSchema),
    next_step_key: z.string().min(1),
    auto_completed: z.boolean(),
    result_key: assessmentResultKeySchema.nullable(),
  })
  .strict();
export type EvaluateAssessmentData = z.infer<typeof evaluateAssessmentDataSchema>;
export const evaluateAssessmentResponseSchema = apiEnvelope(evaluateAssessmentDataSchema);

export const completeAssessmentDataSchema = z
  .object({
    session: assessmentSessionSchema,
    result_key: assessmentResultKeySchema,
    phq9_total: z.number().int().min(0).max(27).nullable(),
    gad7_total: z.number().int().min(0).max(21).nullable(),
  })
  .strict();
export type CompleteAssessmentData = z.infer<typeof completeAssessmentDataSchema>;
export const completeAssessmentResponseSchema = apiEnvelope(completeAssessmentDataSchema);

// ─── instrument bounds ──────────────────────────────────────────────────────
//
// The clinician-facing WORDING for these question ids and for the 0–3 Likert
// values is NOT here. It lives with the surface that renders it —
// `apps/atlas/src/features/intake/lib/intake-labels.ts` — because ADR-017 covers
// this package and forbids hardcoded display copy in it, while `apps/atlas` is
// deliberately outside its `applies_to`. What stays here is the vocabulary
// several workspaces must agree on: the question-id enum above (identifiers,
// which ADR-017 Rule 1 exempts) and the numeric bounds below.

/**
 * Maximum for each stored score, so a consumer can render `4 / 6` rather than a
 * bare `4` — a clinician should not have to know each instrument's range to read
 * the number. Mirrors the `assessment_sessions` CHECK bounds.
 */
export const assessmentScoreMaximums = {
  phq2Subscore: 6,
  gad2Subscore: 6,
  phq9Total: 27,
  gad7Total: 21,
} as const satisfies Record<string, number>;
export type AssessmentScoreKey = keyof typeof assessmentScoreMaximums;
